import { Box, Button, Stack } from '@mui/material'
import { identity, ifElse, pick, pipe, propIs } from 'ramda'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Case, Switch, When } from 'react-if'
import { useNavigate, useParams } from 'react-router-dom'

import { VailAccordion, VailSwitch, VailTextField } from '@/components'
import LanguageSelector from '@/components/LanguageSelector'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useOrg, useUrlParams } from '@/hooks'
import { BasicCustomComponentSettings } from '@/screens/ComponentLibrary/components'
import {
  useCreateCustomComponent,
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { safeCypressID } from '@/utils'
import { ComponentType } from '@/utils/api'
import modifyPath from '@/utils/modifyPath'

import { localizedArray, SupportedLocales } from '../../types/customComponents'

export type InlineSignupSettings = {
  showName: boolean
  showLastName: boolean
  showDOB: boolean
  showPatientID: boolean
  FirstNamePrompt?: localizedArray | null
  LastNamePrompt?: localizedArray | null
  DateOfBirthPrompt?: localizedArray | null
  PatientIdentifierPrompt?: localizedArray | null
}
export type InlineSignupForm = {
  settings: InlineSignupSettings
  name: string
  description: string
}

export const Settings = () => {
  const { orgID } = useParams()
  const { componentID } = useComponentProvider()
  const navigate = useNavigate()
  const org = useOrg(orgID)
  const [{ lang = 'en' }] = useUrlParams()
  const languagesSupported = org.data?.languagesSupported || ['en']

  const current = useGetCustomComponent<InlineSignupSettings>(componentID)
  const create = useCreateCustomComponent<InlineSignupSettings>(orgID)
  const update = useUpdateCustomComponent<InlineSignupSettings>(componentID)
  const form = useForm<InlineSignupForm>({
    defaultValues: {
      settings: {
        showDOB: true,
        showLastName: true,
        showName: true,
        showPatientID: true,
      },
    },
  })
  const { reset } = form
  useEffect(() => {
    if (!current.data) return
    const { name, description, settings } = current.data
    const localizeString = (str: string) =>
      ifElse(propIs(String, str), modifyPath([str], [str, 'en', 0]), identity)

    const updateSettings = pipe(
      localizeString('FirstNamePrompt'),
      localizeString('LastNamePrompt'),
      localizeString('DateOfBirthPrompt'),
      localizeString('PatientIdentifierPrompt')
    )

    reset({
      description,
      name,
      settings: updateSettings({
        ...settings,
        showDOB: !!settings.DateOfBirthPrompt,
        showLastName: !!settings.LastNamePrompt,
        showName: !!settings.FirstNamePrompt,
        showPatientID: !!settings.PatientIdentifierPrompt,
      }),
    })
  }, [current.data, reset])

  const onSubmit = form.handleSubmit(async (data) => {
    const dataToSave = {
      ...pick(['name', 'description'], data),
      settings: pick(
        [
          'FirstNamePrompt',
          'LastNamePrompt',
          'DateOfBirthPrompt',
          'PatientIdentifierPrompt',
        ],
        data.settings
      ),
    }

    if (componentID)
      return await update.mutateAsync({ ...dataToSave, id: componentID })
    const res = await create.mutateAsync({
      ...dataToSave,
      type: ComponentType.inlineSignup,
    })
    return navigate(`../${res?.id}`)
  })
  const onChangeSwitch =
    (registerField: keyof InlineSignupSettings) => (open: boolean) => {
      if (open) form.register(`settings.${registerField}`)
      else form.setValue(`settings.${registerField}`, null)
    }
  return (
    <FormProvider {...form}>
      <Stack component="form" height="100%" rowGap={2} onSubmit={onSubmit}>
        <Button
          id={safeCypressID('save inlineSignUp')}
          sx={{ alignSelf: 'flex-end', mb: 2 }}
          type="submit"
        >
          Save
        </Button>
        <BasicCustomComponentSettings
          componentType={ComponentType.inlineSignup}
        />
        <VailAccordion defaultExpanded title="Configuration">
          <LanguageSelector />
          <Box
            columnGap={1}
            display="grid"
            gridTemplateAreas={{
              md: "'showName firstName' 'showLastName lastName' 'showDOB dob' 'showPatientID patientID'",
              xs: "'showName''firstName''showLastName''lastName''showDOB''dob''showPatientID''patientID'",
            }}
            rowGap={2}
          >
            <VailSwitch
              label="First Name"
              name="settings.showName"
              sx={{ gridArea: 'showName' }}
              onChange={onChangeSwitch('FirstNamePrompt')}
            />
            <When condition={form.watch('settings.showName')}>
              <Switch>
                {languagesSupported.map((orgLang) => (
                  <Case key={orgLang} condition={orgLang === lang}>
                    <VailTextField
                      control={form.control}
                      label="First Name Prompt"
                      name={`settings.FirstNamePrompt.${
                        orgLang as SupportedLocales
                      }.0`}
                      sx={{ gridArea: 'firstName' }}
                    />
                  </Case>
                ))}
              </Switch>
            </When>
            <VailSwitch
              label="Last Name"
              name="settings.showLastName"
              sx={{ gridArea: 'showLastName' }}
              onChange={onChangeSwitch('LastNamePrompt')}
            />
            <When condition={form.watch('settings.showLastName')}>
              <Switch>
                {languagesSupported.map((orgLang) => (
                  <Case key={orgLang} condition={orgLang === lang}>
                    <VailTextField
                      control={form.control}
                      label="Last Name Prompt"
                      name={`settings.LastNamePrompt.${
                        orgLang as SupportedLocales
                      }.0`}
                      sx={{ gridArea: 'lastName' }}
                    />
                  </Case>
                ))}
              </Switch>
            </When>
            <VailSwitch
              label="Date of Birth"
              name="settings.showDOB"
              sx={{ gridArea: 'showDOB' }}
              onChange={onChangeSwitch('DateOfBirthPrompt')}
            />
            <When condition={form.watch('settings.showDOB')}>
              <Switch>
                {languagesSupported.map((orgLang) => (
                  <Case key={orgLang} condition={orgLang === lang}>
                    <VailTextField
                      control={form.control}
                      label="Date of Birth Prompt"
                      name={`settings.DateOfBirthPrompt.${
                        orgLang as SupportedLocales
                      }.0`}
                      sx={{ gridArea: 'dob' }}
                    />
                  </Case>
                ))}
              </Switch>
            </When>
            <VailSwitch
              label="Patient Identifier"
              name="settings.showPatientID"
              sx={{ gridArea: 'showPatientID' }}
              onChange={onChangeSwitch('PatientIdentifierPrompt')}
            />
            <When condition={form.watch('settings.showPatientID')}>
              <Switch>
                {languagesSupported.map((orgLang) => (
                  <Case key={orgLang} condition={orgLang === lang}>
                    <VailTextField
                      control={form.control}
                      label="Patient Identifier Prompt"
                      name={`settings.PatientIdentifierPrompt.${
                        orgLang as SupportedLocales
                      }.0`}
                      sx={{ gridArea: 'patientID' }}
                    />
                  </Case>
                ))}
              </Switch>
            </When>
          </Box>
        </VailAccordion>
      </Stack>
    </FormProvider>
  )
}
